import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { lazyPage } from '../../utils/lazyPage';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BookOpen, Boxes, Briefcase, Compass, FlaskConical, FolderOpen, Globe, Layers, Map as MapIcon, Mic, Package, Rocket,
  Target,
} from 'lucide-react';
import { useAuth } from '../Auth';
import PageHeader from './PageHeader';
import Overview from './Overview';
import Roadmap from './roadmap/Roadmap';
import ProposalTab from './ProposalTab';
import {
  AttributesTab, DocumentsTab, FinancesTab, JournalTab, MeetingsTab, PlmTab,
} from './tabs';
import useProjectData from './useProjectData';
import ReasonDialog from './roadmap/ReasonDialog';
import { ExplorationProvider, LocksGlance } from '../Exploration/light';
import ProgrammeChip from '../Programmes/ProgrammeChip';
import { useProjectProgramme } from '../Programmes/api';
import { focusParent } from './team';
import { toIso, todayDay } from './roadmap/dates';

// Onglets du module Exploration chargés à la demande (React Flow et les vues ne pèsent pas sur la fiche des autres).
const ExplorationAttributesTab = lazyPage(() => import('../Exploration').then((m) => ({ default: m.AttributesTab })));
const ExplorationTab = lazyPage(() => import('../Exploration').then((m) => ({ default: m.ExplorationTab })));
const ExplorationArtefactsTab = lazyPage(() => import('../Exploration').then((m) => ({ default: m.ArtefactsTab })));
const ProgrammeTab = lazyPage(() => import('../Programmes/ProgrammeTab'));
const PortalTab = lazyPage(() => import('./tabs/PortalTab'));
const TabLoading = () => <div className="h-64 animate-pulse rounded-2xl bg-slate-200/50" />;

// Nouvelle fiche projet (déploiement progressif : Employees.can_use_new_project). Un en-tête compact, puis des
// onglets : l'aperçu pour l'essentiel, la roadmap, et le reste du projet. L'onglet ouvert est dans l'adresse
// (?tab=roadmap&iteration=12), pour les liens et le bouton retour.

const TABS = [
  { key: 'apercu', label: 'Aperçu', icon: Compass },
  { key: 'roadmap', label: 'Roadmap', icon: MapIcon },
  { key: 'journal', label: 'Journal', icon: BookOpen },
  { key: 'reunions', label: 'Réunions', icon: Mic },
  { key: 'documents', label: 'Documents', icon: FolderOpen },
  { key: 'finances', label: 'Finances', icon: Briefcase },
  { key: 'attributs', label: 'Attributs', icon: Target },
  { key: 'materiel', label: 'PLM', icon: Package },
];
// L'onglet Outils (le panneau Guttenberg) est retiré le 2026-11-02 : Guttenberg est coupé ; ses liens mènent à l'aperçu.
// Module Exploration (accès réglé par un admin, activé projet par projet) : l'onglet Attributs devient celui du module
// (l'ancien est caché), puis Exploration et, une fois activé, Artefacts.
// Le Carnet (la tambouille interne de Nexus) n'a plus d'onglet ; l'Exploration devient « Verrous » (2026-10-08), puis
// « Arbre » : l'arbre des fonctions, chaque branche avec ses verrous (2026-10-14), puis « Architecture » (2026-11-02),
// sa vue par défaut ; la clé « arbre » reste pour les liens.
const EXPLORATION_TABS = [
  { key: 'attributs', label: 'Attributs', icon: Target },
  { key: 'arbre', label: 'Architecture', icon: Boxes },
  { key: 'artefacts', label: 'Artefacts', icon: FlaskConical },
];
// Anciens liens (?tab=exploration, ?tab=verrous, ?tab=carnet) : vers leur nouvelle place.
const RENAMED = { exploration: 'arbre', verrous: 'arbre', carnet: 'apercu', outils: 'apercu' };
// Programme (projets réunis parce qu'ils sont les étapes d'un même effort) : un onglet de plus, après l'exploration.
const PROGRAMME_TAB = { key: 'programme', label: 'Programme', icon: Layers };
// Projet en proposition (Rémy, 2026-10-21) : on arrive sur l'onglet Proposition (préparer, puis kickoff) ; ce qui ne
// sert qu'une fois le projet lancé (journal, PLM) n'apparaît pas encore.
const PROPOSAL_TAB = { key: 'proposition', label: 'Proposition', icon: Rocket };
// Portail client : échanges avec le client pour toute l'équipe ; invitations et publications pour les administrateurs.
const PORTAL_TAB = { key: 'portail', label: 'Portail client', icon: Globe };
const LATER_TABS = ['journal', 'materiel'];
const isProposal = (project) => project?.status === 'proposition';
const tabsFor = (exploration, programme, project) => {
  let tabs = isProposal(project) ? [PROPOSAL_TAB, ...TABS.filter((t) => !LATER_TABS.includes(t.key))] : TABS;
  if (exploration?.access) {
    const extra = EXPLORATION_TABS.filter((t) => t.key !== 'artefacts' || exploration.enabled);
    const base = tabs.filter((t) => t.key !== 'attributs');
    const at = base.findIndex((t) => t.key === 'roadmap') + 1;
    tabs = [...base.slice(0, at), ...extra, ...base.slice(at)];
  }
  if (programme?.access && programme.programme) {
    const journal = tabs.findIndex((t) => t.key === 'journal');
    const at = journal >= 0 ? journal : tabs.findIndex((t) => t.key === 'reunions');
    tabs = [...tabs.slice(0, at), PROGRAMME_TAB, ...tabs.slice(at)];
  }
  return tabs;
};
// Liens de l'ancienne fiche (?panel=…) vers les onglets.
const PANELS = {
  journal: 'journal', meetings: 'reunions', carnet: 'apercu', drive: 'documents', tools: 'apercu',
  expenses: 'finances', affaires: 'finances', 'project-items': 'materiel', iterations: 'roadmap',
};

const ProjectPage = () => {
  const { projectId } = useParams();
  const id = Number(projectId);
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const data = useProjectData(id, user);
  const { project, iterations, employees, circles, error } = data;
  const [exploration, setExploration] = useState(null);
  const programme = useProjectProgramme(id);
  const tabs = useMemo(() => [...tabsFor(exploration, programme.data, project), PORTAL_TAB],
    [exploration, programme.data, project]);
  const home = isProposal(project) ? 'proposition' : 'apercu'; // l'onglet par défaut

  const day = todayDay();
  const today = toIso(day);
  const tab = useMemo(() => {
    const raw = params.get('tab') || PANELS[params.get('panel')] || (params.get('iterationId') ? 'roadmap' : null);
    const asked = RENAMED[raw] || raw;
    if (['arbre', 'artefacts'].includes(asked) && exploration === null) return asked; // le statut du module arrive
    if (asked === 'programme' && programme.data === null) return asked; // le programme du projet arrive
    return tabs.some((t) => t.key === asked) ? asked : home;
  }, [params, tabs, exploration, programme.data, home]);
  const selection = useMemo(() => {
    const iterationId = Number(params.get('iteration') || params.get('iterationId')) || null;
    return iterationId ? { iterationId, sprintId: Number(params.get('sprint')) || undefined } : null;
  }, [params]);

  // Retour sur l'aperçu ou la roadmap après un passage ailleurs (finances, attributs…) : on rafraîchit ce qu'ils montrent.
  const previousTab = useRef(tab);
  const { reload } = data;
  useEffect(() => {
    if (previousTab.current === tab) return;
    previousTab.current = tab;
    if (tab === 'apercu') { reload.overview(); reload.iterations(); reload.project(); }
    if (tab === 'roadmap') { reload.iterations(); reload.objectives(); reload.attributes(); reload.targets(); }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTab = useCallback((key, extra = {}) => {
    const next = new URLSearchParams();
    if (key !== home) next.set('tab', key);
    Object.entries(extra).forEach(([k, v]) => { if (v != null) next.set(k, String(v)); });
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setParams, home]);

  const select = useCallback((next) => {
    const query = new URLSearchParams({ tab: 'roadmap' });
    if (next?.iterationId) query.set('iteration', String(next.iterationId));
    if (next?.sprintId) query.set('sprint', String(next.sprintId));
    setParams(query, { replace: true });
  }, [setParams]);

  const openIteration = useCallback((iterationId, sprintId) => openTab('roadmap', { iteration: iterationId, sprint: sprintId }),
    [openTab]);

  if (!project) {
    return (
      <div className="mx-auto max-w-[1500px] space-y-4 px-1 py-4 sm:px-4 lg:px-6">
        <div className="h-14 w-72 animate-pulse rounded-2xl bg-slate-200/70" />
        <div className="h-9 animate-pulse rounded-xl bg-slate-200/50" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.65fr_1fr]">
          <div className="h-64 animate-pulse rounded-2xl bg-slate-200/50" />
          <div className="h-64 animate-pulse rounded-2xl bg-slate-200/50" />
        </div>
        {error && <p className="text-sm text-rose-600">{error}</p>}
      </div>
    );
  }

  const tabProps = {
    projectId: id, project, iterations, employees, circles, params,
    refreshProject: data.reload.project, refreshIterations: data.reload.iterations,
  };

  const explorationTab = exploration?.access && ['attributs', 'arbre', 'artefacts'].includes(tab);
  // Vues en canevas (architecture et carte de l'arbre ; C/K et carte du programme) : toute la largeur.
  const wide = (explorationTab && tab === 'arbre' && !['liste', 'historique', 'connaissances'].includes(params.get('vue')))
    || (tab === 'programme' && ['ck', 'carte'].includes(params.get('vue')));

  return (
    <ExplorationProvider key={id} projectId={id} employees={employees} objectives={data.objectives} onStatus={setExploration}>
    <div className="min-h-screen bg-slate-50" key={location.pathname}>
      <div className={`mx-auto ${wide ? 'max-w-none' : 'max-w-[1500px]'} px-1 pb-10 pt-3 sm:px-4 sm:pt-5 lg:px-6`}>
        <PageHeader project={project} clients={data.clients} circles={circles} user={user} actions={data.actions} navigate={navigate}
          extra={<ProgrammeChip projectId={id} projectName={project.name} state={programme} openTab={openTab} />} />

        <nav className="sticky top-0 z-30 -mx-1 mt-4 border-b border-slate-200 bg-slate-50/95 px-1 backdrop-blur sm:-mx-4 sm:px-4 lg:-mx-6 lg:px-6">
          <div className="flex gap-1 overflow-x-auto" role="tablist">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => openTab(key)}
                className={`flex shrink-0 items-center gap-1.5 border-b-2 px-2.5 py-2.5 text-sm font-medium transition ${tab === key
                  ? 'border-indigo-600 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                <Icon size={15} className={tab === key ? 'text-indigo-600' : 'text-slate-400'} />
                {label}
              </button>
            ))}
          </div>
        </nav>

        {error && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
            <button type="button" onClick={data.clearError} className="text-xs font-semibold hover:underline">Fermer</button>
          </div>
        )}

        <main className="mt-4">
          {tab === 'apercu' && (
            <>
              <Overview data={data} today={today} openTab={openTab} openIteration={openIteration} navigate={navigate}
                parentCircle={focusParent(project, circles)} />
              <LocksGlance openTab={openTab} />
            </>
          )}
          {tab === 'proposition' && <ProposalTab data={data} openTab={openTab} navigate={navigate} />}
          {tab === 'roadmap' && <Roadmap data={data} today={day} selection={selection} onSelect={select} projectId={id} navigate={navigate} user={user} />}
          {tab === 'journal' && <JournalTab {...tabProps} />}
          {tab === 'reunions' && <MeetingsTab {...tabProps} />}
          {tab === 'documents' && <DocumentsTab {...tabProps} />}
          {tab === 'finances' && <FinancesTab {...tabProps} steering={data.steering} />}
          {tab === 'attributs' && !exploration?.access && exploration !== null && <AttributesTab {...tabProps} />}
          {explorationTab && (
            <Suspense fallback={<TabLoading />}>
              {tab === 'attributs' && <ExplorationAttributesTab />}
              {tab === 'arbre' && <ExplorationTab />}
              {tab === 'artefacts' && <ExplorationArtefactsTab />}
            </Suspense>
          )}
          {tab === 'programme' && programme.data?.programme && (
            <Suspense fallback={<TabLoading />}>
              <ProgrammeTab projectId={id} programmeState={programme} />
            </Suspense>
          )}
          {tab === 'materiel' && <PlmTab {...tabProps} />}
          {tab === 'portail' && (
            <Suspense fallback={<TabLoading />}><PortalTab projectId={id} /></Suspense>
          )}
        </main>
      </div>
      <ReasonDialog request={data.reasonRequest} onAnswer={data.answerReason} />
    </div>
    </ExplorationProvider>
  );
};

export default ProjectPage;
